import type { KeyboardEvent, ReactNode } from 'react'
import { ChevronLeft, ChevronRight, Download, ImageOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { PHONE_QUERY, useMediaQuery } from '@/hooks/use-media-query'

/** One picture the viewer can show: `url` is null when it could not be loaded for preview. */
export type ViewerImage = {
  key: string
  name: string
  /** Shown beside the name, e.g. "26.0 KB". */
  detail?: string
  url: string | null
}

/**
 * A picture opened from an email: a dialog on wider screens, a bottom sheet on phones (USA-1623).
 * Arrows (and the arrow keys) step through the other pictures of the same email.
 */
export function ImageViewer({
  images,
  index,
  onIndexChange,
  onDownload,
}: {
  images: ViewerImage[]
  /** The picture shown; null closes the viewer. */
  index: number | null
  onIndexChange: (index: number | null) => void
  onDownload: (image: ViewerImage) => void
}) {
  const sheet = useMediaQuery(PHONE_QUERY)
  const image = index === null ? undefined : images[index]
  const open = !!image
  const close = (next: boolean) => {
    if (!next) onIndexChange(null)
  }
  const step = (by: number) => {
    if (index === null) return
    const next = index + by
    if (next >= 0 && next < images.length) onIndexChange(next)
  }
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowLeft') step(-1)
    if (event.key === 'ArrowRight') step(1)
  }

  const body: ReactNode = image && (
    <>
      <div className="flex min-h-40 flex-1 items-center justify-center overflow-hidden rounded-md bg-muted/50 p-2">
        {image.url ? (
          <img
            key={image.key}
            src={image.url}
            alt={image.name}
            className="max-h-[60dvh] max-w-full object-contain sm:max-h-[70vh]"
          />
        ) : (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <ImageOff className="size-4" aria-hidden /> No preview
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        {images.length > 1 && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Previous image"
              disabled={index === 0}
              onClick={() => step(-1)}
            >
              <ChevronLeft />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Next image"
              disabled={index === images.length - 1}
              onClick={() => step(1)}
            >
              <ChevronRight />
            </Button>
          </div>
        )}
        <Button variant="outline" size="sm" className="ml-auto" onClick={() => onDownload(image)}>
          <Download /> Download
        </Button>
      </div>
    </>
  )
  const title = image && (
    <>
      <span className="truncate">{image.name}</span>
      {image.detail && (
        <span className="shrink-0 text-xs font-normal text-muted-foreground tabular-nums">
          {image.detail}
        </span>
      )}
    </>
  )

  if (sheet) {
    return (
      <Sheet open={open} onOpenChange={close}>
        <SheetContent
          side="bottom"
          aria-describedby={undefined}
          onKeyDown={onKeyDown}
          className="flex max-h-[90dvh] flex-col gap-3 rounded-t-xl p-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
        >
          <SheetTitle className="flex min-w-0 items-baseline gap-2 pr-8 text-sm font-medium">
            {title}
          </SheetTitle>
          {body}
        </SheetContent>
      </Sheet>
    )
  }
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        aria-describedby={undefined}
        onKeyDown={onKeyDown}
        className="flex max-h-[90vh] flex-col gap-3 p-4 sm:max-w-3xl"
      >
        <DialogTitle className="flex min-w-0 items-baseline gap-2 pr-8 text-sm font-medium leading-normal">
          {title}
        </DialogTitle>
        {body}
      </DialogContent>
    </Dialog>
  )
}
